import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { Smartphone, Apple, Download, ArrowRight, Share2, Copy, Check, MapPin } from 'lucide-react';

const isIOS = () =>
  typeof navigator !== 'undefined' &&
  /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

const isAndroid = () =>
  typeof navigator !== 'undefined' &&
  /Android/i.test(navigator.userAgent);

const Install = () => {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);

  // QR encodes whichever origin the user is currently on — so it works on
  // localhost, the preview URL, the deployed emergent.host URL, and the
  // user's custom domain once it's mapped, without any code change.
  const appUrl = useMemo(() => {
    if (typeof window === 'undefined') return '';
    return window.location.origin + '/';
  }, []);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(appUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      console.warn('[install] clipboard copy failed:', e);
    }
  };

  // If user lands here from their phone, also offer a direct "Open App" CTA
  const onPhone = isIOS() || isAndroid();

  // Hide install banners if app is already running standalone
  const [standalone, setStandalone] = useState(false);
  useEffect(() => {
    if (typeof window !== 'undefined') {
      setStandalone(
        window.matchMedia?.('(display-mode: standalone)').matches ||
          window.navigator.standalone === true
      );
    }
  }, []);

  return (
    <div className="min-h-screen bg-[#FFF7E6]" data-testid="install-page">
      {/* Hero */}
      <header className="border-b-4 border-black bg-black text-white">
        <div className="max-w-5xl mx-auto px-5 py-5 flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 hover:opacity-80"
          >
            <img src="/lfj-logo.png" alt="" width={32} height={32} style={{ borderRadius: 4 }} />
            <span
              className="font-black text-lg tracking-tight uppercase"
              style={{ fontFamily: 'Cabinet Grotesk' }}
            >
              LUGGAGEFREEJOURNEY
            </span>
          </button>
          <div className="hidden md:flex items-center gap-2 text-xs font-mono uppercase">
            <MapPin size={14} />
            South India · 15 hubs
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-5 py-10 md:py-16">
        <div className="grid md:grid-cols-5 gap-8 md:gap-12 items-start">
          {/* LEFT — pitch & CTA */}
          <section className="md:col-span-3 order-2 md:order-1">
            <h1
              className="text-4xl md:text-5xl lg:text-6xl font-black uppercase leading-[0.95]"
              style={{ fontFamily: 'Cabinet Grotesk' }}
            >
              Get the
              <br />
              <span className="bg-[#FF4500] px-3 py-1 inline-block mt-2">Luggagefreejourney</span>
              <br />
              app on your phone
            </h1>

            <p className="mt-6 text-base md:text-lg font-mono text-gray-800 max-w-xl">
              Pre-book your luggage 24 hours in advance — delivered before you arrive.
              No app store needed. Scan, tap "Install" and you're done.
            </p>

            {/* URL row */}
            <div className="mt-8 card-brutalist p-3 md:p-4 bg-white flex items-center justify-between gap-3 max-w-xl">
              <code
                data-testid="install-app-url"
                className="text-xs md:text-sm font-mono break-all flex-1"
              >
                {appUrl}
              </code>
              <button
                data-testid="install-copy-url"
                onClick={handleCopy}
                className="btn-secondary py-1.5 px-3 text-xs font-mono flex items-center gap-1 flex-shrink-0"
                title="Copy URL"
              >
                {copied ? <Check size={14} color="#00C853" /> : <Copy size={14} />}
                {copied ? 'COPIED' : 'COPY'}
              </button>
            </div>

            {/* Direct-action buttons (only really useful on phone) */}
            {onPhone && !standalone && (
              <a
                data-testid="install-open-app"
                href="/"
                className="mt-4 inline-flex items-center gap-2 btn-primary px-5 py-3"
              >
                <ArrowRight size={18} />
                OPEN THE APP NOW
              </a>
            )}

            {/* Platform instructions */}
            <div className="mt-10 grid sm:grid-cols-2 gap-4">
              <div className="card-brutalist p-4 bg-white" data-testid="install-instructions-android">
                <div className="flex items-center gap-2 mb-2">
                  <Smartphone size={18} />
                  <h3 className="font-black text-sm uppercase font-mono">Android</h3>
                </div>
                <ol className="text-xs md:text-sm font-mono space-y-1.5 list-decimal list-inside text-gray-800">
                  <li>Scan the QR with your camera</li>
                  <li>Tap the link → app opens in Chrome</li>
                  <li>Tap browser menu (⋮) → <span className="font-bold">Install app</span></li>
                  <li>App icon lands on your home screen</li>
                </ol>
              </div>
              <div className="card-brutalist p-4 bg-white" data-testid="install-instructions-ios">
                <div className="flex items-center gap-2 mb-2">
                  <Apple size={18} />
                  <h3 className="font-black text-sm uppercase font-mono">iPhone / iPad</h3>
                </div>
                <ol className="text-xs md:text-sm font-mono space-y-1.5 list-decimal list-inside text-gray-800">
                  <li>Scan the QR with the Camera app</li>
                  <li>Tap the banner → app opens in Safari</li>
                  <li>Tap the <span className="font-bold">Share</span> button</li>
                  <li>Choose <span className="font-bold">"Add to Home Screen"</span></li>
                </ol>
              </div>
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
              <button
                data-testid="install-share-btn"
                onClick={async () => {
                  if (navigator.share) {
                    try {
                      await navigator.share({
                        title: 'Luggagefreejourney',
                        text: 'Get the Luggagefreejourney app — pre-book luggage pickup 24h in advance.',
                        url: appUrl
                      });
                    } catch (e) { console.warn('[install] native share cancelled/blocked:', e); }
                  } else {
                    handleCopy();
                  }
                }}
                className="btn-secondary px-4 py-2 flex items-center gap-2 text-sm"
              >
                <Share2 size={14} />
                SHARE
              </button>
              <button
                onClick={() => navigate('/')}
                className="text-sm font-mono underline self-center text-gray-700 hover:text-orange-600"
              >
                Continue on web →
              </button>
            </div>
          </section>

          {/* RIGHT — QR code */}
          <aside className="md:col-span-2 order-1 md:order-2">
            <div className="card-brutalist p-5 md:p-7 bg-white text-center sticky top-6" data-testid="install-qr-block">
              <div className="text-[10px] md:text-xs font-mono uppercase tracking-widest text-gray-500 mb-3">
                Point your camera here
              </div>
              <div className="inline-block p-3 bg-white border-4 border-black" data-testid="install-qr-svg">
                <QRCodeSVG
                  value={appUrl}
                  size={220}
                  level="M"
                  includeMargin={false}
                  bgColor="#FFFFFF"
                  fgColor="#000000"
                />
              </div>
              <div className="mt-4">
                <div
                  className="inline-block bg-[#FF4500] px-3 py-1 font-mono uppercase text-xs font-bold border-2 border-black"
                  style={{ fontFamily: 'Cabinet Grotesk' }}
                >
                  <Download size={12} className="inline -mt-0.5 mr-1" />
                  Scan to install
                </div>
              </div>
              <p className="mt-4 text-[11px] md:text-xs font-mono text-gray-600">
                Works on iPhone & Android · No app store required
              </p>
            </div>
          </aside>
        </div>

        {/* Trust strip */}
        <section className="mt-16 pt-10 border-t-4 border-black grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            ['24 h', 'advance booking'],
            ['15', 'hub cities'],
            ['₹3 / km', 'transparent pricing'],
            ['100 %', 'tracked & insured*']
          ].map(([big, small]) => (
            <div key={big}>
              <div className="text-3xl md:text-4xl font-black" style={{ fontFamily: 'Cabinet Grotesk' }}>{big}</div>
              <div className="text-[11px] md:text-xs font-mono uppercase text-gray-700 mt-1">{small}</div>
            </div>
          ))}
        </section>

        <p className="mt-6 text-[10px] font-mono text-gray-500 text-center">
          *Conditions apply. See booking flow for details.
        </p>
      </main>

      <footer className="border-t-4 border-black bg-black text-white py-6">
        <div className="max-w-5xl mx-auto px-5 flex flex-wrap items-center justify-between gap-3">
          <div className="font-black text-sm" style={{ fontFamily: 'Cabinet Grotesk' }}>LUGGAGEFREEJOURNEY</div>
          <div className="text-[11px] font-mono uppercase text-gray-400">© LuggageFreeJourney · India</div>
        </div>
      </footer>
    </div>
  );
};

export default Install;
